import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  CircularProgress,
  Alert,
  Box,
  Typography,
  MenuItem,
  Autocomplete
} from '@mui/material';
import { 
  Practitioner, 
  createPractitioner, 
  updatePractitioner,
  getClinikoPractitioners
} from './practitionerService';

interface PractitionerDialogProps {
  open: boolean;
  onClose: (refreshNeeded: boolean) => void;
  practitioner: Practitioner | null;
}

interface ClinikoPractitioner {
  id: string;
  first_name: string;
  last_name: string;
  title?: string;
  name?: string;
}

const PractitionerDialog: React.FC<PractitionerDialogProps> = ({ 
  open, 
  onClose, 
  practitioner 
}) => {
  const [formData, setFormData] = useState<Partial<Practitioner>>({
    name: '',
    cliniko_id: '',
    title: '',
    specialties: '',
    description: '',
    photo_url: '',
    is_active: true,
    sort_order: 0
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clinikoLoading, setClinikoLoading] = useState(false);
  const [clinikoPractitioners, setClinikoPractitioners] = useState<ClinikoPractitioner[]>([]);
  const [selectedClinikoPractitioner, setSelectedClinikoPractitioner] = useState<ClinikoPractitioner | null>(null);
  
  // Load practitioner data into form when editing
  useEffect(() => {
    if (practitioner) {
      setFormData({
        ...practitioner,
        photo_url: practitioner.photo_url || ''
      });
    } else {
      // Reset form for new practitioner
      setFormData({
        name: '',
        cliniko_id: '',
        title: '',
        specialties: '',
        description: '',
        photo_url: '',
        is_active: true,
        sort_order: 0
      });
    }
  }, [practitioner]);
  
  // Load Cliniko practitioners
  useEffect(() => {
    const fetchClinikoPractitioners = async () => {
      try {
        setClinikoLoading(true);
        const response = await getClinikoPractitioners();
        const practitioners = response.practitioners || [];
        
        setClinikoPractitioners(practitioners);
        
        // If editing, find the matching Cliniko practitioner
        if (practitioner && practitioner.cliniko_id) {
          const match = practitioners.find(p => p.id === practitioner.cliniko_id);
          if (match) {
            setSelectedClinikoPractitioner(match);
          }
        }
      } catch (err: any) {
        console.error('Error loading Cliniko practitioners:', err);
      } finally {
        setClinikoLoading(false);
      }
    };
    
    fetchClinikoPractitioners();
  }, [practitioner]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'is_active' ? checked : name === 'sort_order' ? parseInt(value) || 0 : value
    }));
  };
  
  const handleSelectClinikoPractitioner = (practitioner: ClinikoPractitioner | null) => {
    setSelectedClinikoPractitioner(practitioner);
    
    if (practitioner) {
      const fullName = practitioner.name || 
        `${practitioner.title || ''} ${practitioner.first_name} ${practitioner.last_name}`.trim();
      
      setFormData(prev => ({
        ...prev,
        name: fullName,
        cliniko_id: practitioner.id,
        title: practitioner.title || ''
      }));
    }
  };
  
  const handleSubmit = async () => {
    // Validate form
    if (!formData.name || !formData.cliniko_id) {
      setError('Name and Cliniko Practitioner ID are required');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      if (practitioner) {
        // Update existing practitioner
        await updatePractitioner(practitioner.id, formData);
      } else {
        // Create new practitioner
        await createPractitioner(formData);
      }
      
      onClose(true); // Close with refresh needed
    } catch (err: any) {
      setError(err.message || 'Failed to save practitioner');
      console.error('Error saving practitioner:', err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        {practitioner ? `Edit Practitioner: ${practitioner.name}` : 'Add New Practitioner'}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Select from Cliniko Practitioners
            </Typography>
            <Autocomplete
              options={clinikoPractitioners}
              loading={clinikoLoading}
              value={selectedClinikoPractitioner}
              onChange={(_, newValue) => handleSelectClinikoPractitioner(newValue)}
              getOptionLabel={(option) => 
                option.name || 
                `${option.title || ''} ${option.first_name} ${option.last_name}`.trim()
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliniko Practitioner"
                  fullWidth
                  required
                  helperText="Select a practitioner from your Cliniko account"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {clinikoLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="name"
              label="Practitioner Name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="cliniko_id"
              label="Cliniko Practitioner ID"
              value={formData.cliniko_id}
              onChange={handleChange}
              fullWidth
              required
              disabled={loading}
              helperText="ID from your Cliniko practitioner profile"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="title"
              label="Professional Title"
              value={formData.title}
              onChange={handleChange}
              fullWidth
              disabled={loading}
              helperText="e.g., MD, DO, NP"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="sort_order"
              label="Display Order"
              type="number"
              value={formData.sort_order}
              onChange={handleChange}
              fullWidth
              disabled={loading}
              helperText="Lower numbers appear first"
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="specialties"
              label="Specialties"
              value={formData.specialties}
              onChange={handleChange}
              fullWidth
              disabled={loading}
              helperText="Comma-separated list of specialties"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="description"
              label="Bio/Description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              disabled={loading}
              helperText="Practitioner biography or description"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="photo_url"
              label="Photo URL"
              value={formData.photo_url}
              onChange={handleChange}
              fullWidth
              disabled={loading}
              helperText="URL to the practitioner's photo"
            />
          </Grid>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  disabled={loading}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={() => onClose(false)} 
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {practitioner ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PractitionerDialog;